import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { Component, Input, OnInit, OnChanges, ChangeDetectorRef, Output, EventEmitter, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  email,
  phoneLengthValidator,
  patternValidator,
  required,
  ComputedFrom,
  generateGuid,
  multiSelectRequired,
  trimmingDot,
} from '@certemy/common';
import {
  NumberMaskConfig,
  MultiSelect,
  MASK_TYPE,
  MessageQueryService,
  getFileErrorString,
  LoaderService,
} from '@certemy/ui-components';
import _get from 'lodash-es/get';
import _groupBy from 'lodash-es/groupBy';
import _size from 'lodash-es/size';
import _toArray from 'lodash-es/toArray';
import _cloneDeep from 'lodash-es/cloneDeep';
import _intersection from 'lodash-es/intersection';

import {
  DynamicFormField,
  DynamicFormFieldOption,
  DynamicFormSection,
  DynamicFormAttachment,
  DynamicFormSectionFields,
} from 'interfaces/dynamic-form-field.interface';
import { constants, LOG_HALF_UNIT_PATTERN, ONE_HUNDREDTH_PATTERN } from 'constants/constants';
import { FileService } from 'services/file/file.service';
import { Selectors } from 'features/profiles-state/+state/profiles-state.state';
import { DYNAMIC_FORM_ELEMENTS_OWN, DYNAMIC_FORM_ELEMENTS_PROVIDED_BY } from 'constants/workflow.constants';
import { getRolesNamesForField } from 'utils/helpers';
import { BatchRegistryUploadError } from 'features/organization-settings/registry/batch-registry-upload/batch-registry-upload.model';
import { StepService } from 'services/professional/step/step.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, OnChanges, OnDestroy {
  private userRoleId = null;
  private PROVIDED_BY_DEFAULT = DYNAMIC_FORM_ELEMENTS_PROVIDED_BY.ORGANIZATION;
  readonly FILE_SIZE = constants.FILE_DEFAULT_MAX_SIZE;

  @Output() updateFieldValue = new EventEmitter();
  @Input() value;
  @Input() disabled = false;
  @Input() showErrors = false;
  @Input() displayMessages = false;
  @Input() isZeroValueAllowed = false;
  @Input() formId: number;
  @Input() showLabelFilledForAll = false; // show label Filled by... for all roles, not only for others
  @Input() ignoreFilledBy = false; // ignore disabled by role and hide filled label in any case
  @Input() sortByRole = false; // sort sections by Filled by... the fields you need to fill in will be on top
  @Input() sections: DynamicFormSection[] = [];
  @Input() fieldSpecificErrors: BatchRegistryUploadError[] = [];
  @Input() showAllCustomErrors: boolean = false;
  @Input() set elements(elems) {
    this.fields = _toArray(_cloneDeep(elems))
      .sort(this.sortFieldsByOrder)
      .map((field, _, arr) => this.prepareField(field, _, arr));
  }

  get elements(): DynamicFormField[] {
    return this.fields;
  }

  private fieldsIdsChangeValue = [];
  private pushInitValue = [];
  private dependentControlIdsWatchValueChange = {};
  numberMaskConfig: NumberMaskConfig = {
    digitLimit: 12,
    maskType: MASK_TYPE.LEADING_ZERO,
  };
  phoneMaskConfig: NumberMaskConfig = {
    maskType: MASK_TYPE.PHONE_NUMBER,
    digitLimit: 10,
    prefix: '',
  };

  @ComputedFrom('elements', 'sections')
  get sectionsFields(): DynamicFormSectionFields[] {
    if (_size(this.sections)) {
      return this.groupedFields;
    }

    const resultArr = [];
    if (this.sortByRole) {
      const ownElements = this.elements.filter((elem) => !elem.filledByLabel);
      const alienElements = this.elements.filter((elem) => elem.filledByLabel);
      if (ownElements.length) {
        resultArr.push(this.createUndefinedSection(ownElements, DYNAMIC_FORM_ELEMENTS_OWN.OWN));
      }
      if (alienElements.length) {
        resultArr.push(this.createUndefinedSection(alienElements, DYNAMIC_FORM_ELEMENTS_OWN.ALIEN));
      }
    } else {
      resultArr.push(this.createUndefinedSection(this.elements));
    }

    return resultArr.sort(this.sortSectionsByProvidedAndOrder);
  }

  @ComputedFrom('elements', 'sections')
  get groupedFields() {
    const groupedElements = _groupBy(this.elements, 'sectionId');
    const resultArr = [];
    const elementsNoSection = this.elements.filter((element) => !element.sectionId);
    if (this.sortByRole) {
      const ownElements = elementsNoSection.filter((elem) => !elem.filledByLabel);
      const alienElements = elementsNoSection.filter((elem) => elem.filledByLabel);
      if (ownElements.length) {
        resultArr.push(this.createUndefinedSection(ownElements, DYNAMIC_FORM_ELEMENTS_OWN.OWN));
      }
      if (alienElements.length) {
        resultArr.push(this.createUndefinedSection(alienElements, DYNAMIC_FORM_ELEMENTS_OWN.ALIEN));
      }
    } else {
      resultArr.push(this.createUndefinedSection(elementsNoSection));
    }
    const groupedSections = this.sections.reduce((sectionList, section: DynamicFormSection) => {
      if (!groupedElements[section.id]) {
        return sectionList;
      }

      const elements = groupedElements[section.id];
      const providedBy = elements[0].providedBy || this.PROVIDED_BY_DEFAULT;
      const arr = [];
      if (this.sortByRole) {
        const ownElements = elements.filter((elem) => !elem.filledByLabel);
        const alienElements = elements.filter((elem) => elem.filledByLabel);
        if (ownElements.length) {
          arr.push({ ...section, elements: ownElements, providedBy, own: DYNAMIC_FORM_ELEMENTS_OWN.OWN });
        }
        if (alienElements.length) {
          arr.push({ ...section, elements: alienElements, providedBy, own: DYNAMIC_FORM_ELEMENTS_OWN.ALIEN });
        }
      } else {
        arr.push({ ...section, elements, providedBy });
      }

      return [...sectionList, ...arr];
    }, []);

    return [...resultArr, ...groupedSections].sort(this.sortSectionsByProvidedAndOrder);
  }

  dynamicForm: UntypedFormGroup;
  FIELD_TYPES = constants.FORM_FIELD_TYPE_IDS;
  MULTI_SELECT_VALUE_TYPE = MultiSelect.ValueType;
  multiSelectParams = {
    withSearch: false,
    noContentConfig: null,
    withChips: false,
    withSelectAll: true,
    optionDetailColumns: [],
  };
  numericFieldValidation;
  errors: string[] = [];

  private readonly errorTexts = {
    requiredError: 'Please fill in all required fields.',
    emailError: 'Please enter the valid email.',
    numberError: 'Please provide valid number.',
  };
  private fields: DynamicFormField[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private fileService: FileService,
    private loaderService: LoaderService,
    private changeDetector: ChangeDetectorRef,
    private store: Store<any>,
    private messageService: MessageQueryService,
    private stepService: StepService,
  ) {}

  ngOnInit() {
    this.dynamicForm = this.fb.group(this.createForm());

    this.fieldsIdsChangeValue.forEach((id) => {
      this.dynamicForm.controls[id].valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(this.onUpdateFieldValue.bind(this, this.dynamicForm.controls[id]));
    });

    Object.keys(this.dependentControlIdsWatchValueChange).forEach((id) => {
      this.dynamicForm.controls[id].valueChanges.pipe(untilDestroyed(this)).subscribe((selectId) => {
        const field = this.elements.find((data) => data.id === +id);

        if (
          field.subType === constants.SELECT_SUB_TYPES.COUNTRY &&
          field.type.id === constants.STEP_CONTENT_ELEMENTS_TYPES.SELECT
        ) {
          return this.onDependencyControlCountryValueChange(field, selectId);
        }
        return this.onDependencyControlValueChange(id);
      });
    });

    this.pushInitValue.forEach((id) => {
      const control = this.dynamicForm.controls[id];
      if (control) {
        control.setValue(control.value);
      }
    });

    if (this.disabled) {
      this.dynamicForm.disable();
    }

    if (this.value) {
      this.dynamicForm.patchValue(this.value);
    }

    if (this.showErrors) {
      this.checkFormForErrors();
    }

    this.store
      .select(Selectors.currentProfile)
      .pipe(untilDestroyed(this))
      .subscribe((userProfileData) => {
        this.userRoleId = userProfileData.role_id;
        this.fields = this.fields.map(this.prepareField.bind(this));
      });
  }

  ngOnDestroy() {}

  ngOnChanges(params) {
    this.toogleMode(params);
  }

  onMultiSelectFormValuesChange(newFormValues, multiSelectId, updateFieldValue?: boolean) {
    const multiSelectFormControl = this.dynamicForm.controls[multiSelectId];

    if (multiSelectFormControl) {
      multiSelectFormControl.setValue(newFormValues);
      if (updateFieldValue) {
        this.onUpdateFieldValue(multiSelectFormControl);
      }
      this.dynamicForm.markAsDirty();
    }
  }

  private onUpdateFieldValue(control: AbstractControl) {
    const formControlEntry = Object.entries(this.dynamicForm.controls).find(
      ([index, formControl]) => formControl === control,
    );
    if (formControlEntry) {
      const [key, { value }] = formControlEntry;

      this.updateFieldValue.emit({
        field: {
          id: key,
          value,
        },
        elements: this.elements,
        controls: this.dynamicForm.controls,
      });
    }
  }

  setStateOptions(element: DynamicFormField, childElement: DynamicFormField, countryIndex: number): void {
    const control = this.dynamicForm.controls[childElement.id];
    this.stepService
      .getStateOptionsFromField(childElement.id, {
        countryName: element.elementOptions[countryIndex].value,
      })
      .pipe(take(1))
      .subscribe((data) => {
        childElement.elementOptions = data.map((item) => ({
          id: item.optionId,
          name: item.name,
          value: item.name,
        }));
        childElement.hidden = false;
        if (!data.filter((item) => item.optionId === control.value).length) {
          control.setValue('');
        }
        this.changeDetector.detectChanges();
        if (this.dynamicForm.controls[element.id].disabled && !element._isSequence) {
          control.enabled && control.disable();
        } else {
          control.disabled && control.enable();
        }
      });
  }

  private onDependencyControlCountryValueChange(element, id: string) {
    const dependentControlsIndexes = this.dependentControlIdsWatchValueChange[element.id];
    const countryIndex = element.elementOptions.findIndex((data) => {
      return data.id === +id;
    });
    dependentControlsIndexes.forEach((index) => {
      const childElement: DynamicFormField = this.elements[index];
      const control = this.dynamicForm.controls[childElement.id];
      childElement.dependencyData[0].optionId = id;
      if (countryIndex !== -1) {
        element._defValue = element.value;
        this.setStateOptions(element, childElement, countryIndex);
      } else {
        if (childElement._defValue) {
          control.setValue(childElement._defValue);
        }
      }
    });
  }

  private onDependencyControlValueChange(id: string) {
    const dependentControlsIndexes = this.dependentControlIdsWatchValueChange[id];
    dependentControlsIndexes.forEach((index) => {
      const element: DynamicFormField = this.elements[index];
      element.hidden = !element.dependencyData.every((data) => {
        const { fieldId, optionId } = data;
        return this.dynamicForm.controls[fieldId].value === optionId;
      });
      const control = this.dynamicForm.controls[element.id];
      if (element.hidden) {
        element.value = null;
        const defaultValue = this.getValueDependOnType(element);
        control.setValue(defaultValue);
        control.enabled && control.disable();
      } else {
        if (element._defValue) {
          control.setValue(element._defValue);
        }
        control.disabled && control.enable();
      }
    });
  }

  private getValueDependOnType(element: DynamicFormField) {
    let value;
    if (element.type.id === this.FIELD_TYPES.RADIO) {
      value = element.value || element.value === 0 ? element.value : null;
    } else if (element.type.id === this.FIELD_TYPES.SELECT) {
      value = element.value || element.value === 0 ? element.value : '';
    } else if (element.type.id === this.FIELD_TYPES.NUMBER) {
      const elemValue = parseFloat(element.value as string);
      const isValueValid = elemValue === 0 ? this.isZeroValueAllowed : !!element.value;
      value = isValueValid ? element.value : '';
    } else if (element.type.id === this.FIELD_TYPES.ATTACHMENT) {
      value = element.value || [];
    } else {
      value = element.value || '';
    }
    return value;
  }

  private createForm(elements = this.elements) {
    return elements.reduce((formControls, element: DynamicFormField, index) => {
      const value = this.getValueDependOnType(element);
      const disabled = !!element.hidden;
      if (element.isWatchChangeValue) {
        const dependentField = this.elements.find(
          (data) => data.dependencyData && element.id === data.dependencyData[0].fieldId,
        );
        if (
          !(
            element.subType === constants.SELECT_SUB_TYPES.COUNTRY &&
            element.type.id === constants.STEP_CONTENT_ELEMENTS_TYPES.SELECT
          ) ||
          !dependentField
        ) {
          this.fieldsIdsChangeValue.push(element.id);
        }
        if (element.isPushInitValue) {
          this.pushInitValue.push(element.id);
        }
      }
      if (_size(element.dependencyData)) {
        element.dependencyData.forEach((data) => {
          // for dynamic sequence form
          const isFieldExist = elements.find(({ id }) => id === data.fieldId);
          if (!isFieldExist) {
            return;
          }
          const arr = this.dependentControlIdsWatchValueChange[data.fieldId] || [];
          this.dependentControlIdsWatchValueChange = {
            ...this.dependentControlIdsWatchValueChange,
            [data.fieldId]: [...arr, index],
          };
        });
      }

      let validators = [];

      if (element.required && element.type.id) {
        if (element.type.id === this.FIELD_TYPES.MULTI_SELECT) {
          validators = [...validators, multiSelectRequired];
        } else {
          validators = [...validators, required];
        }
      }

      if (element.type.id === this.FIELD_TYPES.EMAIL) {
        validators = [...validators, email];
      }

      if (element.type.id === this.FIELD_TYPES.NUMBER) {
        validators = [...validators, trimmingDot()];
      }

      if (element.type.id === this.FIELD_TYPES.PHONE_NUMBER) {
        validators = [...validators, phoneLengthValidator(10)];
      }

      if (element.minFieldValue) {
        validators = [...validators, Validators.minLength(element.minFieldValue)];
      }

      if (element.maxFieldValue) {
        validators = [...validators, Validators.maxLength(element.maxFieldValue)];
      }

      if (element.validators && element.validators.length) {
        validators = [...validators, ...element.validators];
      }

      formControls[element.id] = [
        {
          value,
          disabled,
        },
        Validators.compose(validators),
      ];

      return formControls;
    }, {});
  }

  private sortFieldsByOrder(a: DynamicFormField, b: DynamicFormField): number {
    return a.order - b.order;
  }

  private sortSectionsByProvidedAndOrder(a: DynamicFormField, b: DynamicFormField): number {
    const { order: orderA, providedBy: providedByA, own: ownA } = a;
    const { order: orderB, providedBy: providedByB, own: ownB } = b;
    if (ownA !== ownB) {
      return ownB - ownA;
    } else if (providedByA !== providedByB) {
      return providedByB - providedByA;
    } else {
      return orderA - orderB;
    }
  }

  private setAttachmentValue(field): DynamicFormAttachment[] {
    const value = field.value || field.files;
    return _size(value) ? value.map((file) => ({ ...file, id: generateGuid() })) : [];
  }

  getElementId(element: DynamicFormField): string {
    return `form_${element.id}${this.formId || ''}`;
  }

  getOptionId(element: DynamicFormField, option: DynamicFormFieldOption): string {
    return `form_${element.id}_${option.value}`;
  }

  checkRequiredFields() {
    const requiredError = !!this.elements.find((element) => {
      const { errors } = this.dynamicForm.controls[element.id];
      return element.required && errors && errors.required;
    });

    if (requiredError) {
      this.errors = [...this.errors, this.errorTexts.requiredError];
    }

    return requiredError;
  }

  checkEmails() {
    const emailError = !!this.elements.find((element) => {
      const isEmail = element.type.id === this.FIELD_TYPES.EMAIL;
      const control = this.dynamicForm.controls[element.id];

      if (isEmail && control.invalid) {
        return control.errors.hasOwnProperty('email');
      }
    });

    if (emailError) {
      this.errors = [...this.errors, this.errorTexts.emailError];
    }

    return emailError;
  }

  checkNumberFields() {
    this.elements.forEach((element) => {
      const isNumber = element.type.id === this.FIELD_TYPES.NUMBER;
      const control = this.dynamicForm.controls[element.id];

      if (isNumber && control.invalid && control.value.length) {
        control.setValue((+control.value).toString());
      }
    });
  }

  checkLength() {
    const lengthErrorTextArr = [];

    this.elements.forEach(({ id, label }) => {
      const control = this.dynamicForm.controls[id];
      if (!label) {
        return;
      }
      if (control.invalid && _intersection(['minlength', 'maxlength'], Object.keys(control.errors)).length) {
        lengthErrorTextArr.push(`Field ${label} value is not correct, please verify`);
      }
    });

    this.errors = [...this.errors, ...lengthErrorTextArr];

    return lengthErrorTextArr.length;
  }

  checkCustomError() {
    if (this.showAllCustomErrors) {
      return this.elements.forEach((element) => {
        const control = this.dynamicForm.controls[element.id];
        if (
          ((!control.errors || !Object.values(control.errors).length) &&
            (!element.validators || !element.validators.length)) ||
          control.valid
        ) {
          return false;
        }

        const customErrors = control.errors
          ? (Object.values(control.errors).filter((error) => typeof error === 'string') as string[])
          : [];
        this.errors = [...this.errors, ...customErrors];
      });
    }
    return !!this.elements.find((element) => {
      const control = this.dynamicForm.controls[element.id];
      if (!element.validators || !element.validators.length || control.valid) {
        return false;
      }

      const customErrors = control.errors
        ? (Object.values(control.errors).filter((error) => typeof error === 'string') as string[])
        : [];
      this.errors = [...this.errors, ...customErrors];
      return customErrors.length > 0;
    });
  }

  checkFormForErrors(): boolean {
    const form = this.dynamicForm;
    this.errors = [];
    const isValid = form.disabled || form.valid;
    if (isValid) {
      this.showErrors = false;
    } else {
      this.showErrors = true;
      this.checkRequiredFields();
      this.checkEmails();
      this.checkLength();
      this.checkNumberFields();
      this.checkCustomError();
    }
    return isValid;
  }

  getFormErrorMessages(): string[] {
    this.errors = [];
    this.checkRequiredFields();
    this.checkEmails();
    this.checkLength();
    this.checkNumberFields();
    this.checkCustomError();
    return this.errors;
  }

  restoreChanges(value?) {
    this.dynamicForm.reset();
    this.elements.forEach((element) => {
      switch (element.type.id) {
        case constants.STEP_CONTENT_ELEMENTS_TYPES.ATTACHMENT:
          element.value = value ? value[element.id] : [];
          delete element.draftValue;
          this.dynamicForm.controls[element.id].setValue(element.value);
          break;
        case constants.STEP_CONTENT_ELEMENTS_TYPES.MULTI_SELECT:
          element.value = '';
          this.dynamicForm.controls[element.id].setValue(element.value);
          break;
      }
    });
    if (value) {
      this.dynamicForm.patchValue(value);
    }
    this.cleanUpErrorFlags();
  }

  getFormData(form = this.dynamicForm, checkDisabledFlag = false) {
    // for select form.value method returns a string ("0"). it should be a number
    return this.elements.map((element: DynamicFormField) => {
      let value;
      // if we set disabled property via set filledByDisabled
      // we need get value from getRawValue if user role is included in filledByRoleIds array
      if (
        (element.filledByDisabled &&
          _size(element.filledByRoleIds) &&
          element.filledByRoleIds.includes(this.userRoleId)) ||
        (checkDisabledFlag && element.disabled)
      ) {
        value = form.getRawValue()[element.id];
      } else {
        value = form.value[element.id];
      }
      const isAttachment = element.type.id === constants.STEP_CONTENT_ELEMENTS_TYPES.ATTACHMENT;

      if (element.type.id === constants.STEP_CONTENT_ELEMENTS_TYPES.SELECT) {
        value = value ? Number(value) : '';
      }

      if (isAttachment) {
        value = null;
      }

      return {
        id: element.id,
        value,
        ...(isAttachment ? { files: element.value } : {}),
      };
    });
  }

  updateFormData(data, isDisabled?: boolean) {
    this.elements = data;
    this.dynamicForm = this.fb.group(this.createForm());
    if (isDisabled) {
      this.dynamicForm.disable();
    }
  }

  private toogleMode(params) {
    if (this.dynamicForm && params.disabled && params.disabled.currentValue !== params.disabled.previousValue) {
      switch (params.disabled.currentValue) {
        case true:
          this.dynamicForm.disable();
          break;
        case false:
          this.dynamicForm.enable();
          break;
      }
      this.fields = this.fields.map(this.prepareField.bind(this));
      this.cleanUpErrorFlags();
    }
  }

  private cleanUpErrorFlags() {
    this.showErrors = false;
  }

  private prepareField(field: any, _, arr) {
    if (field.type.id === this.FIELD_TYPES.NUMBER && field.isDefault) {
      field.validators = [
        ...(field.validators || []),
        patternValidator(
          field.oneHundredthValidator ? ONE_HUNDREDTH_PATTERN : LOG_HALF_UNIT_PATTERN,
          `Please provide valid number of ${field.label.toLowerCase() || 'hours'}.`,
        ),
      ];
      field.min = field.min || 0.5;
      field.step = field.step || 0.5;
      field.hint = field.hint || 'Value should be multiple of 0.5';
    }

    if (_size(field.dependencyData)) {
      field.hidden = !field.dependencyData.every((data) => {
        const { fieldId, optionId } = data;
        const fieldDependUpon = arr.find((_f) => _f.id === fieldId);
        return fieldDependUpon && fieldDependUpon.value === optionId;
      });
      if (field.value) {
        field._defValue = _cloneDeep(field.value);
      }
    }

    if (field.type.id === this.FIELD_TYPES.ATTACHMENT) {
      field.value = this.setAttachmentValue(field);
    }

    if (_get(field.elementOptions, 'length')) {
      field.elementOptions = field.elementOptions.map((option) => ({
        ...option,
        id: option.id,
        name: option.value || option.name,
      }));
    }

    delete field.disabledByRole;
    if (
      !this.ignoreFilledBy &&
      (_size(field.filedByRoleIds) || field.filledByDisabled || field.disabled) &&
      this.userRoleId
    ) {
      const disabled =
        field.filledByDisabled || (field.filedByRoleIds && !field.filedByRoleIds.includes(this.userRoleId));
      if (disabled) {
        field.disabledByRole = true;
      }
      field.filledByLabel =
        field.filledByLabel ||
        (_size(field.filedByRoleIds) && (disabled || this.showLabelFilledForAll)
          ? `Filled by ${getRolesNamesForField(field.filedByRoleIds)}`
          : '');
      if (this.dynamicForm && !this.disabled) {
        const control = this.dynamicForm.controls[field.id];
        if (disabled || field.disabled || field.hidden) {
          control.enabled && control.disable();
        } else {
          control.disabled && control.enable();
        }
      }
    }

    const parentField = this.fields.find((item) => field.dependencyData && field.dependencyData[0].fieldId === item.id);

    if (
      field.subType === constants.SELECT_SUB_TYPES.STATE &&
      field.type.id === constants.STEP_CONTENT_ELEMENTS_TYPES.SELECT &&
      parentField &&
      !parentField._defValue
    ) {
      if (parentField.value) {
        const countyIndex = parentField.elementOptions.findIndex((data) => data.id === parentField.value);
        this.setStateOptions(parentField, field, countyIndex);
      } else {
        field.hidden = true;
      }
    }

    return field;
  }

  createUndefinedSection(elements: DynamicFormField[], own?: DYNAMIC_FORM_ELEMENTS_OWN) {
    return {
      id: null,
      name: '',
      order: 0,
      providedBy: this.PROVIDED_BY_DEFAULT,
      elements,
      own,
    };
  }

  changeShowInDigitalWallet(showInDigitalWallet, element) {
    element.showInDigitalWallet = showInDigitalWallet;
    if (element.checkDigitalWallet) {
      element.value = element.value.map((value) => ({
        ...value,
        isVisibleForProfessional: showInDigitalWallet || false,
      }));
      this.dynamicForm.controls[element.id].setValue(element.value);
      element.draftValue = (element.draftValue || []).map((value) => ({
        ...value,
        isVisibleForProfessional: showInDigitalWallet || false,
      }));
      this.changeDetector.detectChanges();
    }
  }

  onAttachmentUpload(element, files: File[]) {
    this.setAttachmentValueFromFormControl(element);
    element.draftValue = [...(element.draftValue || element.value || [])];
    if (!Array.isArray(files) || !files.length) {
      return;
    }
    files = files.reduce((arr, file) => {
      if (getFileErrorString(file, this.FILE_SIZE)) {
        const { name, size } = file;
        element.draftValue = [...element.draftValue, { name, size, id: generateGuid() }];
      } else {
        arr.push(file);
      }
      return arr;
    }, []);
    forkJoin(
      files.map((file) =>
        this.loaderService.wrapObservable(this.fileService.upload(file, file.name)).pipe(
          catchError((err) => {
            const errorDetails = _get(err, 'body.error_details', 'Cannot upload file');
            this.messageService.addErrorMessage(errorDetails);
            return of('error');
          }),
        ),
      ),
    ).subscribe((attachments) => {
      const values = attachments.reduce((arr: any[], attachment, index) => {
        if (attachment === 'error') {
          return arr;
        }
        const { name, size } = files[index];
        const { link } = attachment[0];
        const value: any = { link, name, size, id: generateGuid() };
        if (element.checkDigitalWallet) {
          value.isVisibleForProfessional = element.showInDigitalWallet || false;
        }
        arr = [...arr, value];
        return arr;
      }, []);
      element.value = [...(element.value ?? []), ...values];
      this.dynamicForm.controls[element.id].setValue(element.value);
      element.draftValue = [...element.draftValue, ...values];
      this.dynamicForm.markAsDirty();
      this.changeDetector.detectChanges();
    });
  }

  onAttachmentRemove(element, fileToRemove: DynamicFormAttachment) {
    this.setAttachmentValueFromFormControl(element);
    element.value = element.value.filter((value) => value.id !== fileToRemove.id);
    this.dynamicForm.controls[element.id].setValue(element.value);
    if (_size(element.draftValue)) {
      element.draftValue = element.draftValue.filter((value) => value.id !== fileToRemove.id);
    } else {
      element.draftValue = [...(element.value || [])];
    }
  }

  private setAttachmentValueFromFormControl(element) {
    const controlValue = this.dynamicForm.controls[element.id].value;
    if (!_size(element.value) && _size(controlValue)) {
      element.value = controlValue;
    }
    return element;
  }

  getDirtyValues(form: UntypedFormGroup = this.dynamicForm) {
    const dirtyValues = {};

    Object.keys(form.controls).forEach((key) => {
      const currentControl = form.controls[key];

      if (currentControl.dirty) {
        if ((currentControl as UntypedFormGroup).controls) {
          dirtyValues[key] = this.getDirtyValues(currentControl as UntypedFormGroup);
        } else {
          dirtyValues[key] = currentControl.value;
        }
      }
    });
    return dirtyValues;
  }
}
